import React, { useEffect, useState, useCallback } from "react";
import {
	Box,
	capitalize,
	Card,
	CardContent,
	FormControl,
	Grid,
	MenuItem,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import { MyCalendarIcon, MyCreditCardIcon } from "../../Assets/svg/MyIcons";
import { listTripolaPlansForDropdownAction } from "../../Actions/TripolaPlanActions";
import { CustomSwitch } from "../../Assets/customTheme/customswitch";

const StatCard = ({ icon: Icon, title, value, bgColor, iconBgColor }) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			p: 2.5,
			borderRadius: 3,
			bgcolor: bgColor,
		}}
	>
		<Box
			sx={{
				width: 50,
				height: 50,
				background: iconBgColor,
				borderRadius: 2,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				mr: 2,
			}}
		>
			<Icon size={22} />
		</Box>
		<Box>
			<Typography
				variant="caption"
				sx={{
					color: "text.secondary",
					display: "block",
				}}
			>
				{title}
			</Typography>
			<Typography variant="body1" sx={{ fontWeight: 600 }}>
				{value}
			</Typography>
		</Box>
	</Box>
);

const TripolaPlanStructureStep = ({
	formik,
	selectedCurrency
}) => {
	const [tripolaPlanList, setTripolaPlanList] = useState([]);

	const selectedTripolaPlan = tripolaPlanList.find(
		(plan) => String(plan.id) === String(formik.values.tripolaPlanId)
	);

	const fetchTripolaPlansDropDownList = useCallback(async () => {
		try {
			const response = await listTripolaPlansForDropdownAction();
			setTripolaPlanList(response?.data?.data || []);
		} catch (error) {
			console.error("error fetching tripola plans ->", error);
		}
	}, []);

	useEffect(() => {
		fetchTripolaPlansDropDownList();
	}, [fetchTripolaPlansDropDownList]);

	// ========================================================================
	// RENDER
	// ========================================================================
	return (
		<Grid container spacing={2}>
			{/* Left Column: Form */}
			<Grid item xs={12} md={7}>
				{/* Plan Selection */}
				<FormControl fullWidth sx={{ mb: 3 }}>
					<Typography variant="subtitle2" gutterBottom> Select Plan <span className="text-danger ms-1">*</span></Typography>
					<Select
						name="tripolaPlanId"
						value={formik.values.tripolaPlanId || ""}
						onChange={(e) => {
							const selectedPlanId = e.target.value;

							formik.setFieldValue("tripolaPlanId", selectedPlanId);

							const selectedPlan = tripolaPlanList.find(
								(plan) => String(plan.id) === String(selectedPlanId)
							);

							if (selectedPlan?.selectedBillingCycle) {
								formik.setFieldValue(
									"tripolaBillingCycle",
									selectedPlan.selectedBillingCycle
								);
							} else {
								formik.setFieldValue("tripolaBillingCycle", "");
							}
						}}
						displayEmpty
						renderValue={(selected) => {
							if (!selected)
								return <em>Select a plan</em>;
							const plan = tripolaPlanList.find(
								(p) => String(p.id) === String(selected)
							);
							return plan ? plan.plan_name : "";
						}}
						disabled={Boolean(tripolaPlanList.length === 0)}
					>
						<MenuItem value="" disabled>
							<em>Select a plan</em>
						</MenuItem>
						{tripolaPlanList.map((plan) => (
							<MenuItem
								key={`plan-${plan.id}`}
								value={plan.id}
							>
								{plan.plan_name}
							</MenuItem>
						))}
					</Select>
					{formik.touched.tripolaPlanId && formik.errors.tripolaPlanId && (
						<Typography variant="caption" color="error">
							{formik.errors.tripolaPlanId}
						</Typography>
					)}
				</FormControl>

				{/* Billing Cycle */}
				<FormControl fullWidth sx={{ mb: 3 }}>
					<Typography variant="subtitle2" gutterBottom> Billing Cycle<span className="text-danger ms-1">*</span></Typography>
					<Select
						name="tripolaBillingCycle"
						value={formik.values.tripolaBillingCycle || ""}
						onChange={formik.handleChange}
						displayEmpty
						disabled={Boolean(!formik.values.tripolaPlanId)}
						renderValue={(selected) => {
							if (!selected) return <em>Select billing cycle</em>;
							return selected;
						}}
					>
						<MenuItem value="" disabled>
							<em>Select billing cycle</em>
						</MenuItem>
						<MenuItem value="monthly">
							Monthly
						</MenuItem>
						<MenuItem value="yearly">
							Yearly
						</MenuItem>
					</Select>
					{formik.touched.tripolaBillingCycle &&
						formik.errors.tripolaBillingCycle && (
							<Typography variant="caption" color="error">
								{formik.errors.tripolaBillingCycle}
							</Typography>
						)}
				</FormControl>

				<Grid item xs={12} sm={6}>
					<Box sx={{ mt: 2 }}>
						<Typography variant="subtitle2" gutterBottom> Enable Auto Pay </Typography>
						<CustomSwitch
							name="tripolaIsAutoPay"
							checked={formik.values.tripolaIsAutoPay}
							onChange={(event) =>
								formik.setFieldValue("tripolaIsAutoPay", event.target.checked)
							}
						/>
					</Box>
				</Grid>
			</Grid>

			{/* Right Column: Plan Preview */}
			<Grid item xs={12} md={5}>
				{/* Plan Header */}
				<Box
					sx={{
						bgcolor: "grey.50",
						p: 2.5,
						borderRadius: 1,
						mb: 3,
						boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
					}}
				>
					<Box sx={{ textAlign: "center", mb: 3 }}>
						<Box
							sx={{
								width: 90,
								height: 90,
								background: "linear-gradient(135deg, #1976d2, #3f51b5)",
								borderRadius: "50%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								color: "#fff",
								mx: "auto",
								mb: 2,
							}}
						>
							<MyCreditCardIcon size={36} className="text-white" />
						</Box>
					</Box>
					<Box sx={{ textAlign: "center" }}>
						<Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5 }}>
							<Box
								sx={{
									display: "inline-flex",
									alignItems: "center",
									gap: 1,
								}}
							>
								{selectedTripolaPlan?.plan_name || "No Plan Selected"}
							</Box>
						</Typography>

						{formik.values?.tripolaBillingCycle && (
							<Box
								sx={{
									display: "flex",
									alignItems: "baseline",
									justifyContent: "center",
								}}
							>
								<Typography
									variant="h5"
									sx={{
										fontWeight: 700,
										color: "primary.main",
										mr: 0.5,
									}}
								>
									{/* {customPlan &&
												isCustomPlan &&
												customPlan.price != null &&
												!isNaN(Number(customPlan.price)) ? (
												<span>
													{currencyDetails?.symbol}{" "}
													{Number(
														customPlan.price
													).toFixed(2)}
												</span>
											) : (
												calculateTotalPrice()
											)} */}
								</Typography>
							</Box>
						)}
					</Box>
				</Box>

				{/* Billing Summary Box */}
				<Box
					sx={{
						bgcolor: "grey.50",
						p: 2.5,
						borderRadius: 1,
						mb: 3,
						boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
					}}
				>
					<Typography
						variant="subtitle1"
						fontWeight={600}
						color="var(--bs-primary)"
						sx={{ mb: 1.5 }}
					>
						Billing Summary
					</Typography>
					<Grid container>
						<Grid item xs={12}>
							<StatCard
								icon={MyCalendarIcon}
								title="Billing Cycle"
								value={
									formik.values?.tripolaBillingCycle
										? capitalize(
											formik.values
												.tripolaBillingCycle
										)
										: "-"
								}
								bgColor="rgba(25, 118, 210, 0.08)"
								iconBgColor="linear-gradient(135deg, #bbdefb, #90caf9)"
							/>
						</Grid>
					</Grid>
				</Box>
			</Grid>
		</Grid>
	);
};

export default TripolaPlanStructureStep;
